import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Building2, History, Save } from "lucide-react";
import { api } from "../api";
import { useAdminAuth } from "../AdminAuthContext";
import {
    Panel, Loading, LoadError, PartnershipBadge, VisibilityBadge,
    PARTNERSHIP_STATUSES, PARTNERSHIP_LABELS, fmtDate, fmtDateTime,
} from "../ui";

const fieldCls =
    "w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors disabled:opacity-50";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]";

export const AdminHospitalDetail = () => {
    const { hospitalId } = useParams();
    const { user } = useAdminAuth();
    const isAdmin = user?.role === "ADMIN";

    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState("");

    useEffect(() => {
        setData(null);
        setForm(null);
        api
            .get(`/admin/hospitals/${hospitalId}`)
            .then((res) => {
                setData(res.data);
                setForm({ ...res.data.hospital });
            })
            .catch(() => setError("Could not load this hospital. It may not exist."));
    }, [hospitalId]);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setSaved(false);
    };

    const save = async () => {
        setSaving(true);
        setSaveError("");
        try {
            const { data: updated } = await api.patch(`/admin/hospitals/${hospitalId}`, {
                legal_name: form.legal_name,
                display_name: form.display_name,
                city: form.city,
                state: form.state,
                country: form.country,
                address: form.address,
                website: form.website,
                phone: form.phone,
                email: form.email || null,
                desk_name: form.desk_name,
                desk_email: form.desk_email || null,
                desk_phone: form.desk_phone,
                description: form.description,
                services: form.services,
                specialties: typeof form.specialties === "string"
                    ? form.specialties.split(",").map((s) => s.trim()).filter(Boolean)
                    : form.specialties,
                partnership_status: form.partnership_status,
                agreement_date: form.agreement_date,
                internal_notes: form.internal_notes,
                active: !!form.active,
                public_visible: !!form.public_visible,
            });
            setData((d) => ({ ...d, hospital: updated }));
            setForm({ ...updated, specialties: (updated.specialties || []).join(", ") });
            setSaved(true);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setSaveError(typeof detail === "string" ? detail : "Could not save. Please check the details and try again.");
        } finally {
            setSaving(false);
        }
    };

    if (error && !data) return <LoadError message={error} />;
    if (!data || !form) return <Loading />;

    const h = data.hospital;
    const specialtiesText = typeof form.specialties === "string"
        ? form.specialties
        : (form.specialties || []).join(", ");

    return (
        <div data-testid="admin-hospital-detail">
            <Link
                to="/admin/hospitals"
                data-testid="hospital-back-link"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] transition-colors"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> All hospitals
            </Link>

            <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-[#F7F5EF]" data-testid="hospital-detail-title">
                        {h.display_name || h.legal_name}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <PartnershipBadge status={h.partnership_status} />
                        <VisibilityBadge visible={h.public_visible} />
                        {h.active ? (
                            <span className="text-xs text-[#64716D]">Active record</span>
                        ) : (
                            <span className="text-xs text-red-400">Inactive record</span>
                        )}
                        <span className="text-xs text-[#64716D]">· {data.referral_count} referral(s)</span>
                    </div>
                </div>
                {isAdmin && (
                    <button
                        type="button"
                        data-testid="hospital-save-button"
                        onClick={save}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                )}
            </div>

            {saved && (
                <p data-testid="hospital-save-success" className="mt-4 rounded-lg border border-emerald-900/60 bg-emerald-950/40 px-4 py-2.5 text-xs text-emerald-300">
                    Saved. Changes are recorded in the audit log.
                </p>
            )}
            {saveError && (
                <p role="alert" data-testid="hospital-save-error" className="mt-4 rounded-lg border border-red-900/60 bg-red-950/40 px-4 py-2.5 text-xs text-red-300">
                    {saveError}
                </p>
            )}
            {isAdmin && !!form.public_visible && (
                <p className="mt-4 rounded-lg border border-amber-900/60 bg-amber-950/40 px-4 py-2.5 text-xs text-amber-300" data-testid="hospital-visibility-warning">
                    This hospital is marked publicly visible. Only enable this for hospitals actually approved as UniversHeal partners.
                </p>
            )}

            <div className="mt-6 grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Panel className="p-5">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <Building2 className="w-4 h-4" /> Basic information
                        </h2>
                        <div className="mt-4 grid sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="h-legal" className={labelCls}>Legal / entity name</label>
                                <input id="h-legal" data-testid="hospital-field-legal-name" value={form.legal_name || ""} onChange={set("legal_name")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-display" className={labelCls}>Display name</label>
                                <input id="h-display" data-testid="hospital-field-display-name" value={form.display_name || ""} onChange={set("display_name")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-city" className={labelCls}>City</label>
                                <input id="h-city" data-testid="hospital-field-city" value={form.city || ""} onChange={set("city")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-state" className={labelCls}>State</label>
                                <input id="h-state" data-testid="hospital-field-state" value={form.state || ""} onChange={set("state")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-country" className={labelCls}>Country</label>
                                <input id="h-country" data-testid="hospital-field-country" value={form.country || ""} onChange={set("country")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-address" className={labelCls}>Address</label>
                                <input id="h-address" data-testid="hospital-field-address" value={form.address || ""} onChange={set("address")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-website" className={labelCls}>Website</label>
                                <input id="h-website" data-testid="hospital-field-website" value={form.website || ""} onChange={set("website")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} placeholder="https://" />
                            </div>
                            <div>
                                <label htmlFor="h-phone" className={labelCls}>Phone</label>
                                <input id="h-phone" data-testid="hospital-field-phone" value={form.phone || ""} onChange={set("phone")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-email" className={labelCls}>Email</label>
                                <input id="h-email" data-testid="hospital-field-email" type="email" value={form.email || ""} onChange={set("email")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-specialties" className={labelCls}>Specialties (comma separated)</label>
                                <input id="h-specialties" data-testid="hospital-field-specialties" value={specialtiesText} onChange={set("specialties")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                        </div>
                    </Panel>

                    <Panel className="p-5">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">International patient desk</h2>
                        <div className="mt-4 grid sm:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="h-desk-name" className={labelCls}>Desk contact name</label>
                                <input id="h-desk-name" data-testid="hospital-field-desk-name" value={form.desk_name || ""} onChange={set("desk_name")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-desk-email" className={labelCls}>Desk email</label>
                                <input id="h-desk-email" data-testid="hospital-field-desk-email" type="email" value={form.desk_email || ""} onChange={set("desk_email")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <div>
                                <label htmlFor="h-desk-phone" className={labelCls}>Desk phone</label>
                                <input id="h-desk-phone" data-testid="hospital-field-desk-phone" value={form.desk_phone || ""} onChange={set("desk_phone")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                        </div>
                    </Panel>

                    <Panel className="p-5">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">Services & description</h2>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label htmlFor="h-services" className={labelCls}>International patient services</label>
                                <textarea id="h-services" data-testid="hospital-field-services" rows={3} value={form.services || ""} onChange={set("services")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} placeholder="e.g. Visa assistance, airport transfers, interpreter services" />
                            </div>
                            <div>
                                <label htmlFor="h-description" className={labelCls}>Description</label>
                                <textarea id="h-description" data-testid="hospital-field-description" rows={3} value={form.description || ""} onChange={set("description")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                        </div>
                    </Panel>

                    <Panel className="p-5">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">Internal notes</h2>
                        <textarea
                            data-testid="hospital-internal-notes"
                            rows={4}
                            value={form.internal_notes || ""}
                            onChange={set("internal_notes")}
                            disabled={!isAdmin}
                            placeholder="Internal only — never shown publicly."
                            className={`mt-4 ${fieldCls}`}
                        />
                    </Panel>
                </div>

                <div className="space-y-6">
                    <Panel className="p-5">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">Partnership & status</h2>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label htmlFor="h-partnership" className={labelCls}>Partnership status</label>
                                <select id="h-partnership" data-testid="hospital-field-partnership" value={form.partnership_status || "PROSPECT"} onChange={set("partnership_status")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`}>
                                    {PARTNERSHIP_STATUSES.map((p) => (
                                        <option key={p} value={p}>{PARTNERSHIP_LABELS[p]}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="h-agreement" className={labelCls}>Agreement date</label>
                                <input id="h-agreement" data-testid="hospital-field-agreement-date" type="date" value={form.agreement_date || ""} onChange={set("agreement_date")} disabled={!isAdmin} className={`mt-1.5 ${fieldCls}`} />
                            </div>
                            <label className="flex items-center justify-between gap-3 rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2.5">
                                <span className="text-sm text-[#F7F5EF]">Record active</span>
                                <input
                                    type="checkbox"
                                    data-testid="hospital-field-active"
                                    checked={!!form.active}
                                    onChange={(e) => {
                                        setForm((f) => ({ ...f, active: e.target.checked }));
                                        setSaved(false);
                                    }}
                                    disabled={!isAdmin}
                                    className="w-4 h-4 cursor-pointer"
                                />
                            </label>
                            <label className="flex flex-col gap-2 rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2.5">
                                <span className="flex items-center justify-between">
                                    <span className="text-sm text-[#F7F5EF]">Publicly visible</span>
                                    <input
                                        type="checkbox"
                                        data-testid="hospital-field-public-visible"
                                        checked={!!form.public_visible}
                                        onChange={(e) => {
                                            setForm((f) => ({ ...f, public_visible: e.target.checked }));
                                            setSaved(false);
                                        }}
                                        disabled={!isAdmin}
                                        className="w-4 h-4 cursor-pointer"
                                    />
                                </span>
                                <span className="text-[11px] leading-relaxed text-[#64716D]">
                                    OFF by default. Enable only for hospitals approved as UniversHeal partners.
                                </span>
                            </label>
                        </div>
                        <dl className="mt-4 space-y-1.5 text-xs text-[#64716D]">
                            <p>Created {fmtDate(h.created_at)}</p>
                            <p>Last updated {fmtDateTime(h.updated_at)}</p>
                        </dl>
                    </Panel>

                    <Panel className="p-5" data-testid="hospital-activity">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <History className="w-4 h-4" /> Activity history
                        </h2>
                        {data.activities.length === 0 ? (
                            <p className="mt-3 text-sm text-[#64716D]">No changes recorded yet.</p>
                        ) : (
                            <ul className="mt-4 space-y-3">
                                {data.activities.map((a, i) => (
                                    <li key={a.id || i} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500/80" />
                                        <div>
                                            <p className="text-sm text-[#F7F5EF]">{a.action}</p>
                                            <p className="text-xs text-[#64716D]">{a.user_name} · {fmtDateTime(a.created_at)}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Panel>
                </div>
            </div>
        </div>
    );
};
